/**
 * Estado de acceso al portal (contrato F1) de un usuario de cliente o partner,
 * con el botón «Desbloquear» del admin.
 *
 * Lo comparten el detalle de cuenta de cliente y el de partner porque la
 * pregunta que responde es la misma en los dos («este usuario no puede entrar,
 * ¿por qué?»): bloqueo por intentos fallidos, intentos acumulados y contraseña
 * temporal aún sin cambiar. Sin datos (backend anterior al contrato F1) no
 * pinta nada — degradar en silencio es preferible a mentir con un «Activo».
 */
import React, { useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { LockOpen, Lock, Key, CheckCircle, Google, Password } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import LoadingButton from './LoadingButton';
import { PortalAccess } from '../types/portalAccess';

interface Props {
  access?: PortalAccess | null;
  /** POST del endpoint de desbloqueo; el llamador recarga el detalle. */
  onUnlock: () => Promise<void>;
}

const fmt = (iso?: string | null): string => {
  if (!iso) return '';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, 'dd/MM/yyyy HH:mm');
};

const PortalAccessPanel: React.FC<Props> = ({ access, onUnlock }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  if (!access) return null;

  const handleUnlock = async () => {
    setLoading(true);
    try {
      await onUnlock();
    } finally {
      setLoading(false);
    }
  };

  const until = fmt(access.locked_until);

  // CÓMO entra. Desde que hay dos puertas es la primera pregunta del admin
  // («¿por qué no le llega ninguna contraseña?»), así que va delante del estado
  // de bloqueo. Un backend anterior al login con Google no manda el campo: se
  // asume contraseña, que es lo que ese backend hace.
  const methods = access.auth_methods && access.auth_methods.length > 0
    ? access.auth_methods
    : ['password'];
  const withPassword = methods.includes('password');
  const withGoogle = methods.includes('google');
  const methodLabel = withGoogle && withPassword
    ? t('portalAccess.methodBoth', 'Contraseña y Google')
    : withGoogle
      ? t('portalAccess.methodGoogle', 'Solo Google')
      : t('portalAccess.methodPassword', 'Contraseña');

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
      <Tooltip
        title={
          withGoogle && access.google_email
            ? t('portalAccess.methodGoogleTooltip', 'Entra con la cuenta de Google {{email}}', {
                email: access.google_email,
              })
            : t('portalAccess.methodTooltip', 'Cómo entra al portal')
        }
      >
        <Chip
          size="small"
          variant="outlined"
          color={withGoogle ? 'primary' : 'default'}
          icon={withGoogle ? <Google sx={{ fontSize: 16 }} /> : <Password sx={{ fontSize: 16 }} />}
          label={methodLabel}
        />
      </Tooltip>

      {access.is_locked ? (
        <Tooltip
          title={
            until
              ? t('portalAccess.lockedUntilTooltip', 'Bloqueada hasta {{until}}', { until })
              : t('portalAccess.lockedTooltip', 'Bloqueada por intentos fallidos')
          }
        >
          <Chip
            size="small"
            color="error"
            icon={<Lock sx={{ fontSize: 16 }} />}
            label={
              until
                ? t('portalAccess.lockedUntil', 'Bloqueada hasta {{until}}', { until })
                : t('portalAccess.locked', 'Bloqueada')
            }
          />
        </Tooltip>
      ) : (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircle sx={{ fontSize: 16 }} />}
          label={t('portalAccess.notLocked', 'Acceso normal')}
        />
      )}

      {access.must_change_password && (
        <Tooltip
          title={t(
            'portalAccess.mustChangeTooltip',
            'Aún no ha entrado a crear su contraseña definitiva: sigue con la temporal de un solo uso.'
          )}
        >
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            icon={<Key sx={{ fontSize: 16 }} />}
            label={t('portalAccess.mustChange', 'Contraseña temporal pendiente')}
          />
        </Tooltip>
      )}

      {access.failed_login_count > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('portalAccess.failedAttempts', '{{count}} intento(s) fallido(s)', {
            count: access.failed_login_count,
          })}
        </Typography>
      )}

      {/* El botón aparece si hay ALGO que limpiar (bloqueo o contador): en una
          cuenta sana no aporta nada y solo añade ruido. */}
      {(access.is_locked || access.failed_login_count > 0) && (
        <LoadingButton
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<LockOpen />}
          loading={loading}
          onClick={handleUnlock}
        >
          {t('portalAccess.unlock', 'Desbloquear')}
        </LoadingButton>
      )}
    </Box>
  );
};

export default PortalAccessPanel;
